import { Route } from '@angular/router';

/** Base URL of the onboarding flow; every key in the maps below sits under it. */
export const FLOW_BASE_PATH = '/kiwisaver/onboarding/flow';

/**
 * Removed flow paths and where they now go, keyed by the segment after `/flow/`.
 *
 * These have no screen any more, so `LEGACY_REDIRECT_ROUTES` turns each into a
 * route redirect (query params survive), and the flow navigator and onboarding
 * resolver read the same entries. Add an entry here when a screen is removed;
 * nothing else needs a hand-written copy of the path.
 */
export const REDIRECTED_PATHS: Readonly<Record<string, string>> = {
  // The disclosure start page was removed; live ads still point at it.
  disclosure: 'goal',
  // Only ever a path in the navigator; it never had a route in this app.
  'preference-selector': 'avoidance',
  // The NZ investments question was removed (BS-576).
  'nz-holdings': 'priority-picker',
  // The recommended-fund screen was removed (BS-584); advice follows reveal.
  'recommended-fund': 'advice',
  // The legacy switch screens were removed (BS-593). Advice is the branch
  // point: Start the switch routes by partner status from there, so a member
  // returning on any old switch URL is re-oriented rather than dropped
  // mid-form. Resolving them to a partner-branch screen would put a
  // non-partner in front of a personal details form, because the resolver
  // cannot know partner status without loading the fund.
  'our-service': 'advice',
  signup: 'advice',
  phone: 'advice',
  pir: 'advice',
  dob: 'advice',
  address: 'advice',
  ird: 'advice',
  manual: 'advice',
  'review-details': 'advice',
  'terms-conditions': 'advice',
};

/**
 * Paths whose screens still render but are in no step list, so a returning user
 * saved on one has nowhere to resume. They map for RESUME ONLY and must never
 * become route redirects, or the screen could not be reached.
 *
 * `rewards-toggle` is switched off in the flow and comes back later; it is the
 * only entry.
 */
export const RESUME_ONLY_PATHS: Readonly<Record<string, string>> = {
  'rewards-toggle': 'profile',
};

/** Every mapped path: what a saved page or a reload on one of them resolves to. */
export const LEGACY_PATHS: Readonly<Record<string, string>> = {
  ...REDIRECTED_PATHS,
  ...RESUME_ONLY_PATHS,
};

/**
 * Route redirects for `REDIRECTED_PATHS`. `redirectTo` is relative, which keeps
 * the query string (`previousFundID`, `utm_*`) on the way through.
 */
export const LEGACY_REDIRECT_ROUTES: Route[] = Object.entries(REDIRECTED_PATHS).map(
  ([path, redirectTo]) => ({ path, redirectTo }),
);

function lookup(map: Readonly<Record<string, string>>, url: string): string {
  const path = url.split('?')[0];
  if (!path.startsWith(`${FLOW_BASE_PATH}/`)) {
    return url;
  }
  const replacement = map[path.slice(FLOW_BASE_PATH.length + 1)];
  return replacement === undefined ? url : `${FLOW_BASE_PATH}/${replacement}`;
}

/**
 * The replacement for a mapped flow URL (full path, query ignored), resume-only
 * paths included, or the url unchanged when it is not mapped.
 */
export function resolveLegacyPath(url: string): string {
  return lookup(LEGACY_PATHS, url);
}

/**
 * Like `resolveLegacyPath` but for the route-redirect subset only: the paths
 * with no screen. Resume-only paths come back unchanged because their screens
 * still render.
 */
export function resolveRedirectedPath(url: string): string {
  return lookup(REDIRECTED_PATHS, url);
}
